import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios'
import { AuthTokens } from '@/types/auth'
import { apiConfig } from './config'

const API_BASE_URL = apiConfig.baseURL

class ApiClient {
  private client: AxiosInstance
  private refreshingToken: Promise<string> | null = null

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
      timeout: 10000,
    })

    this.setupInterceptors()
  }

  private setupInterceptors() {
    // Request interceptor
    this.client.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        const tokens = this.getTokens()
        if (tokens?.access_token) {
          config.headers.Authorization = `Bearer ${tokens.access_token}`
        }
        return config
      },
      (error) => {
        return Promise.reject(error)
      }
    )

    // Response interceptor
    this.client.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config

        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true

          try {
            if (!this.refreshingToken) {
              this.refreshingToken = this.refreshToken()
            }

            const newToken = await this.refreshingToken
            originalRequest.headers.Authorization = `Bearer ${newToken}`
            return this.client(originalRequest)
          } catch (refreshError) {
            this.clearTokens()
            window.location.href = '/login'
            return Promise.reject(refreshError)
          } finally {
            this.refreshingToken = null
          }
        }

        return Promise.reject(error)
      }
    )
  }

  private async refreshToken(): Promise<string> {
    const tokens = this.getTokens()
    if (!tokens?.refresh_token) {
      throw new Error('No refresh token available')
    }

    const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
      refresh_token: tokens.refresh_token,
    })

    const newTokens: AuthTokens = {
      access_token: response.data.access_token,
      refresh_token: response.data.refresh_token,
    }

    this.saveTokens(newTokens)
    return newTokens.access_token
  }

  private getTokens(): AuthTokens | null {
    try {
      const tokens = localStorage.getItem('auth_tokens')
      if (!tokens || tokens === 'undefined') {
        return null
      }
      return JSON.parse(tokens)
    } catch (error) {
      console.error('Failed to parse auth tokens:', error)
      return null
    }
  }

  private saveTokens(tokens: AuthTokens) {
    localStorage.setItem('auth_tokens', JSON.stringify(tokens))
  }

  private clearTokens() {
    localStorage.removeItem('auth_tokens')
    localStorage.removeItem('user')
  }

  // Public methods - bind to ensure proper context
  get = <T = any>(url: string, config?: any) => this.client.get<T>(url, config)
  post = <T = any>(url: string, data?: any, config?: any) => this.client.post<T>(url, data, config)
  put = <T = any>(url: string, data?: any, config?: any) => this.client.put<T>(url, data, config)
  delete = <T = any>(url: string, config?: any) => this.client.delete<T>(url, config)
  patch = <T = any>(url: string, data?: any, config?: any) => this.client.patch<T>(url, data, config)

  // Method to set auth tokens manually (used after login)
  setAuthTokens(tokens: AuthTokens) {
    this.saveTokens(tokens)
  }
}

export default new ApiClient()